import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { AuthorisedUser, Collection, CollectionType, EntityTypeEnum, Order, Severity, ValidationIssue } from 'api'
import { Groupbox } from 'view/components/panels'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { RadioButtonStrip } from 'view/components/buttons'
import { CollectionSelectboxBaseField, CollectionTypeSelectboxField } from 'view/shared/entity'
import { createAuthorisedUserSelector } from 'selectors/connectionSelectors'
import { createInvalidValueValidationIssue, getDefaultCollectionName } from 'model'
import { collectionNameMaxFieldLength } from 'view/styles/constants'
import { Locale, Translation } from 'locale'
import { availableCollectionsSelector } from 'selectors/collectionSelectors'
import { createEntityGetterSelector } from 'selectors/entitySelectors'
import { Nullable } from 'utils'
import { isNotNil } from 'utils/predicate'
import {
  createCollectionByCollectionTypeFilter,
  createCollectionByCollectionTypesFilter,
  createCollectionByCounterpartiesFilter
} from './utils'
import { enabledCollectionTypes } from 'model'

export interface AssignToCollectionPanelProps {
  disabled: boolean
  orderIds: string[]
  collectionId: string | null
  newCollectionName: string | null
  newCollectionType: CollectionType | null
  authorisedUser: AuthorisedUser
  availableCollections: Collection[]
  validationIssues?: ValidationIssue[]
  getOrderById: (id: string) => Nullable<Order>
  onUpdate: (collectionId: string | null, newCollectionName: string | null, newCollectionType: string | null) => unknown
}

const mapStateToProps = createStructuredSelector({
  authorisedUser: createAuthorisedUserSelector(),
  availableCollections: availableCollectionsSelector,
  getOrderById: createEntityGetterSelector(EntityTypeEnum.ORDER)
})

export const AssignToCollectionPanelComponent: React.FunctionComponent<AssignToCollectionPanelProps> = props => {
  const {
    orderIds = [],
    getOrderById,
    collectionId,
    newCollectionName,
    newCollectionType,
    disabled,
    availableCollections,
    authorisedUser,
    onUpdate
  } = props

  const [selectedOption, setSelectedOption] = useState<Translation.TC_EXISTING | Translation.TC_NEW>(Translation.TC_EXISTING)
  const [collectionTypeFilter, setCollectionTypeFilter] = useState<CollectionType | null>(null)
  const [defaultNameChanged, setDefaultNameChanged] = useState<boolean>(false)

  const acceptedCollectionTypes: CollectionType[] | undefined = useMemo(() => {
    const hasAnyOrderPreassignments = orderIds.some(o => {
      const order = getOrderById(o)
      return !!order && order?.nestedLegs?.legs?.length
    })

    return hasAnyOrderPreassignments ? enabledCollectionTypes.filter(c => c === CollectionType.SEND_INDIVIDUALLY) : undefined
  }, [getOrderById, orderIds])

  const matchingNameExists =
    newCollectionName &&
    newCollectionType === CollectionType.SEND_INDIVIDUALLY &&
    availableCollections.some(collection => collection.name === newCollectionName && collection.type === CollectionType.SEND_INDIVIDUALLY)

  const validationIssues = [
    ...(props.validationIssues ?? []),
    ...(matchingNameExists ? [createInvalidValueValidationIssue('newCollectionName', 'Collection name already exists', Severity.WARN)] : [])
  ]

  const updateCollectionId = (value: string | null) => {
    onUpdate(value, null, null)
    const collection = availableCollections.find(availableCollection => availableCollection.id === value)
    if (collection) {
      setCollectionTypeFilter(collection.type)
    }
  }

  const updateCollectionTypeFilter = (value: CollectionType | null) => {
    setCollectionTypeFilter(value)
    onUpdate(null, null, null)
  }

  const updateNewCollectionName = (value: string) => {
    onUpdate(null, value, newCollectionType)
    setDefaultNameChanged(true)
  }

  const updateNewCollectionType = (value: CollectionType) => {
    onUpdate(null, defaultNameChanged ? newCollectionName : getDefaultCollectionName(authorisedUser, value), value)
  }

  const updateSelectedOption = (value: Translation.TC_EXISTING | Translation.TC_NEW) => {
    setSelectedOption(value)
    setCollectionTypeFilter(null)
    setDefaultNameChanged(false)
  }

  useEffect(() => {
    onUpdate(
      null,
      selectedOption === Translation.TC_NEW ? getDefaultCollectionName(authorisedUser) : null,
      selectedOption === Translation.TC_NEW ? CollectionType.SEND_INDIVIDUALLY : null
    )
  }, [authorisedUser, onUpdate, selectedOption])

  const availableCollectionKeys = useMemo(() => {
    // multiple orders with different counterparties is not supported
    const [order] = orderIds.map(getOrderById).filter(isNotNil)
    const counterpartyIds = isNotNil(order) ? order.negotiationInstruction.counterpartyInstructions.map(i => i.counterpartyId) : []

    return availableCollections
      .filter(createCollectionByCollectionTypesFilter(acceptedCollectionTypes))
      .filter(createCollectionByCollectionTypeFilter(collectionTypeFilter))
      .filter(createCollectionByCounterpartiesFilter(counterpartyIds))
      .map(c => c.id)
  }, [acceptedCollectionTypes, availableCollections, collectionTypeFilter, getOrderById, orderIds])

  return (
    <Groupbox title={Locale.translate(Translation.TC_ADD_TO_COLLECTION)} disabled={disabled}>
      <RadioButtonStrip
        options={[Translation.TC_EXISTING, Translation.TC_NEW]}
        selectedOption={selectedOption}
        buttonWidth={60}
        disabled={disabled}
        onUpdate={updateSelectedOption}
        dataTestId={'AssignToCollectionPanel-RadioButtonStrip'}
      />

      {selectedOption === Translation.TC_EXISTING ? (
        <DialogRow>
          <DialogField>
            <CollectionTypeSelectboxField
              label={Locale.translate(Translation.TC_COLLECTION_TYPE)}
              disabled={disabled}
              enabledCollectionTypes={acceptedCollectionTypes}
              canClear={true}
              value={collectionTypeFilter}
              onUpdate={updateCollectionTypeFilter}
              dataTestId={'AssignToCollectionPanel-CollectionTypeSelectboxField-AddToExistingCollection'}
            />
          </DialogField>
          <DialogField>
            <CollectionSelectboxBaseField
              optionKeys={availableCollectionKeys}
              fieldName={'collectionId'}
              label={Locale.translate(Translation.SC_ADD_TO_EXISTING_COLLECTION)}
              placeholder={Locale.translate(Translation.SC_SELECT_COLLECTION)}
              disabled={disabled}
              value={collectionId}
              validationIssues={validationIssues}
              onUpdate={updateCollectionId}
              dataTestId={'AssignToCollectionPanel-CollectionSelectboxField-AddToExistingCollection'}
            />
          </DialogField>
        </DialogRow>
      ) : (
        <DialogRow>
          <DialogField>
            <CollectionTypeSelectboxField
              label={Locale.translate(Translation.TC_COLLECTION_TYPE)}
              placeholder={Locale.translate(Translation.SC_SELECT_COLLECTION_TYPE)}
              disabled={disabled}
              enabledCollectionTypes={acceptedCollectionTypes}
              value={newCollectionType}
              onUpdate={updateNewCollectionType}
              dataTestId={'AssignToCollectionPanel-CollectionTypeSelectboxField-CollectionType'}
            />
          </DialogField>
          <DialogField>
            <TextInputField
              fieldName={'newCollectionName'}
              label={Locale.translate(Translation.SC_NAME_COLLECTION)}
              placeholder={Locale.translate(Translation.SC_UNIQUE_NAME)}
              maxLength={collectionNameMaxFieldLength}
              disabled={disabled}
              value={newCollectionName}
              validationIssues={validationIssues}
              onUpdate={updateNewCollectionName}
              dataTestId={'AssignToCollectionPanel-CollectionNameField-NameCollection'}
            />
          </DialogField>
        </DialogRow>
      )}
    </Groupbox>
  )
}

export const AssignToCollectionPanel = connect(mapStateToProps)(AssignToCollectionPanelComponent)
